<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: RecentChanges                                         |
| Section: views/overviews                                         |
| Purpose: Mixed chronological view of recently updated records.  |
+------------------------------------------------------------------+
-->
<template>
    <div class="wrapper overview-wrapper overview-gap-72">
        <div class="overview-header">
            <h1>{{ $t('recentChanges.title') }}</h1>
            <p class="subtitle">{{ $t('recentChanges.subtitle') }}</p>
        </div>

        <LoadingSpinner v-if="loading" />
        <p v-else-if="loadError" class="no_results">{{ $t('recentChanges.loadError') }}</p>
        <p v-else-if="!recentChanges.length" class="no_results">{{ $t('recentChanges.noResults') }}</p>

        <div v-else class="recent-changes-grid">
            <template v-for="change in recentChanges" :key="change.key">
                <PersonCardNew v-if="change.kind === 'person'" :name="change.name || ''"
                    :years="formatYears(change.birthdate, change.altbirthdate, change.deathdate, change.burialdate, change.living, $i18n.locale)"
                    :gender="change.sex || 'M'" :href="change.personID ? localizedPath(`/person/${change.personID}`) : localizedPath('/login')"
                    :personID="change.personID" :photoSrc="change.photoPath || ''"
                    :relativeDate="formatRelativeDate(change.changedate, $i18n.locale)"
                    :typeLabel="$t('recentChanges.itemType.person')" :living="Boolean(change.living)"
                    :placeholder="change.placeholder" />

                <MediaCard v-else :thumbnail="getCardThumbnail(change)"
                    :label="change.title || $t('recentChanges.noTitle')"
                    :showDate="formatRelativeDate(change.changedate, $i18n.locale)"
                    :typeLabel="$t(`recentChanges.itemType.${change.kind}`)" :url="getCardUrl(change)"
                    :class="{ 'guest-media-card': !isMediaAccessible(change) }"
                    @click="handleMediaClick(change)" />
            </template>
        </div>
    </div>

    <MediaOverlay v-if="showOverlay" :isVisible="showOverlay" :mediaUrl="overlayMedia?.url"
        :mediaType="overlayMedia?.type" :mediaKind="overlayMedia?.kind" :mediaTitle="overlayMedia?.title"
        :mediaOwner="overlayMedia?.owner" :mediaList="overlayMediaArray" :currentMediaIndex="currentMediaIndex"
        :linkedPersonIDs="overlayMedia?.linkedPersonIDs" @close="closeOverlay"
        @navigate="handleMediaNavigation" />

    <ContactCta :heading="$t('recentChanges.ctaTitle')" :buttonLabel="$t('recentChanges.ctaAllRelatives')"
        :href="localizedPath('/stamboom?view=relaties')" :secondaryButtonLabel="$t('recentChanges.ctaAllMedia')"
        :secondaryHref="localizedPath('/fotos-en-documenten')" />
</template>

<script>
import PersonCardNew from '@/atoms/PersonCardNew.vue';
import MediaCard from '@/atoms/MediaCard.vue';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import MediaOverlay from '@/components/MediaOverlay.vue';
import ContactCta from '@/components/ContactCta.vue';
import { getRecentChanges } from '@/api/updatesService';
import { formatYears, formatRelativeDate } from '@/utils/dateUtils';
import { getMediaPath, getMediaThumbnailPath, normalizeOverlayMedia } from '@/utils/mediaUtils';
import { useAuth } from '@/composables/useAuth';
import publicMediaConfig from '@/data/publicMediaConfig.json';
import { applyGenericSeo, applyBreadcrumbSeo } from '@/utils/seoUtils';

export default {
    name: 'RecentChanges',
    components: {
        PersonCardNew,
        MediaCard,
        LoadingSpinner,
        MediaOverlay,
        ContactCta,
    },
    setup() {
        const { authToken } = useAuth();
        return { authToken };
    },
    data() {
        return {
            recentChanges: [],
            loading: true,
            loadError: false,
            showOverlay: false,
            overlayMedia: null,
            overlayMediaArray: [],
            currentMediaIndex: 0,
        };
    },
    created() {
        this.removeLegacyCategoryQuery();
        this.loadRecentChanges();
    },
    mounted() {
        const title = this.$t('recentChanges.seoTitle');
        const description = this.$t('recentChanges.seoDescription');
        const image = 'https://sejarah-thumann.online/img/sejarah_thumann1080x781.4fc54a6a.jpg';
        const url = `${window.location.origin}${this.localizedPath('/nieuw')}`;

        applyGenericSeo({ title, description, image, url }, { includeTwitter: true });
        applyBreadcrumbSeo([
            { name: this.$t('nav.home'), url: `https://sejarah-thumann.online${this.localizedPath('/')}` },
            { name: this.$t('recentChanges.title'), url: `https://sejarah-thumann.online${this.localizedPath('/nieuw')}` },
        ]);
    },
    methods: {
        formatYears,
        formatRelativeDate,
        async loadRecentChanges() {
            this.loading = true;
            this.loadError = false;

            try {
                this.recentChanges = await getRecentChanges(100);
            } catch (error) {
                console.error('Error loading recent changes:', error);
                this.recentChanges = [];
                this.loadError = true;
            } finally {
                this.loading = false;
            }
        },
        removeLegacyCategoryQuery() {
            if (!Object.prototype.hasOwnProperty.call(this.$route.query, 'category')) return;

            const query = { ...this.$route.query };
            delete query.category;
            this.$router.replace({
                path: this.$route.path,
                query,
                hash: this.$route.hash,
            });
        },
        isPublicMedia(title) {
            if (!title) return false;
            return publicMediaConfig.publicKeywords.some(keyword =>
                title.toLowerCase().includes(keyword.toLowerCase())
            );
        },
        isMediaAccessible(media) {
            if (media.kind === 'photo') return Boolean(this.authToken) || !media.living;
            if (media.kind === 'document') return Boolean(this.authToken) || this.isPublicMedia(media.title);
            return false;
        },
        getCardThumbnail(media) {
            return this.isMediaAccessible(media)
                ? getMediaThumbnailPath(media.thumbpath, media.mediaType)
                : 'path/to/guest-thumbnail.jpg';
        },
        getCardUrl(media) {
            return this.isMediaAccessible(media)
                ? getMediaPath(media.path, media.mediaType)
                : this.localizedPath('/login');
        },
        handleMediaClick(media) {
            if (!this.isMediaAccessible(media)) {
                this.$router.push(this.localizedPath('/login'));
                return;
            }

            this.openMediaOverlay(media);
        },
        getFileType(path = '') {
            const extension = path.split('.').pop().toLowerCase();
            return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension) ? 'image' : 'document';
        },
        getOverlayMedia(media) {
            return normalizeOverlayMedia(media, {
                type: this.getFileType(media.path),
                kind: media.mediaType,
                pathResolver: (path, source) => getMediaPath(path, source.mediaType),
                defaultTitle: this.$t('recentChanges.noTitle'),
                defaultOwner: this.$t('recentChanges.unknownOwner'),
            });
        },
        openMediaOverlay(media, mediaArray = null) {
            const accessibleMedia = mediaArray || this.recentChanges.filter(change =>
                change.kind !== 'person' && this.isMediaAccessible(change)
            );

            this.overlayMedia = this.getOverlayMedia(media);
            this.overlayMediaArray = accessibleMedia;
            this.currentMediaIndex = accessibleMedia.findIndex(item => item.key === media.key);
            this.showOverlay = true;
        },
        handleMediaNavigation(newIndex) {
            if (newIndex < 0 || newIndex >= this.overlayMediaArray.length) return;
            this.openMediaOverlay(this.overlayMediaArray[newIndex], this.overlayMediaArray);
        },
        closeOverlay() {
            this.showOverlay = false;
        },
        localizedPath(path) {
            const locale = this.$route?.params?.locale;
            return locale ? `/${locale}${path}` : path;
        },
    },
};
</script>

<style scoped>
.recent-changes-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    align-items: stretch;
    gap: 32px 20px;
    width: 100%;
}

.recent-changes-grid :deep(.media_card) {
    width: auto;
    min-width: 0;
    height: 100%;
}

.recent-changes-grid :deep(.media_card_image),
.recent-changes-grid :deep(.recent-person-card_visual) {
    border-radius: 8px;
}

.no_results {
    width: 100%;
    text-align: center;
}

@media (max-width: 1200px) {
    .recent-changes-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}

@media (max-width: 980px) {
    .recent-changes-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (max-width: 860px) {
    .recent-changes-grid {
        grid-template-columns: minmax(0, 1fr);
        gap: 24px;
    }

    .recent-changes-grid :deep(.media_card) {
        flex-direction: row;
    }

    .recent-changes-grid :deep(.media_card_image) {
        width: 28%;
        min-height: 112px;
        padding: 0;
        flex: 0 0 28%;
        align-self: stretch;
    }
}
</style>
